import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { handleApiError } from "../utils/handleApiError";
import { useFontenehusApi } from "../api/FontenehusApi";
import type { Fontenehus } from "../types/Fontenehus";
import { RefreshCw } from "lucide-react";
import { useUser } from "../context/UserContext";
import { useConfirm } from "../context/ConfirmContext ";

const AdminFontenehusProfile: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getFontenehusById,
    updateFontenehus,
    deleteFontenehus,
    generateFontenehusCode,
  } = useFontenehusApi();
  const { user } = useUser();
  const [targetFontenehus, setTargetFontenehus] = useState<Fontenehus | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [deleteSuggestionDays, setDeleteSuggestionDays] = useState(0);
  const [fontenehusCode, setFontenehusCode] = useState("");
  const isSuperAdmin = user?.roles?.some((r) => ["SuperAdmin"].includes(r));
  const confirm = useConfirm();
  useEffect(() => {
    const loadFontenehus = async () => {
      try {
        const response = await getFontenehusById(Number(id));
        const fh = response.data.data;
        setTargetFontenehus(fh);
        setName(fh.name);
        setFontenehusCode(fh.fontenehusCode);
        setDeleteSuggestionDays(fh.deleteSuggestionDays);
      } catch (err: any) {
        handleApiError(err, "Kunne ikke laste Fontenehuset.");
      } finally {
        setLoading(false);
      }
    };

    loadFontenehus();
  }, [id]);

  const getNewFontenehusCode = async () => {
    const ok = await confirm({
      title: "Endre fontenehuskode",
      message: "Endre kode for Fontenehuset?",
      confirmText: "Ok",
    });
    if (!ok) return;
    try {
      const response = await generateFontenehusCode(targetFontenehus!.id);
      const data = response.data.data;
      toast.success(response.data.message);
      setFontenehusCode(data.newCode);
    } catch (err: any) {
      handleApiError(err, "Feil ved endring av kode for fontenehus.");
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Slett fontenehus",
      message: "Slette dette Fontenehuset?",
      confirmText: "Slett",
    });
    if (!ok) return;

    try {
      await deleteFontenehus(Number(id));
      toast.success("Fontenehus slettet");
      navigate(-1);
    } catch (err: any) {
      handleApiError(err, "Feil ved sletting av fontenehus.");
    }
  };

  const handleSave = async () => {
    if (!targetFontenehus) return;

    // 🔍 Check if anything actually changed
    const hasChanges =
      name !== targetFontenehus.name ||
      fontenehusCode !== targetFontenehus.fontenehusCode ||
      deleteSuggestionDays !== targetFontenehus.deleteSuggestionDays;

    if (!hasChanges) {
      toast.info("Ingen endringer å lagre.");
      return;
    }

    setSaving(true);
    try {
      const response = await updateFontenehus(targetFontenehus.id, {
        name,
        fontenehusCode,
        deleteSuggestionDays,
      });

      toast.success(response.data.message);
      setTargetFontenehus({
        ...targetFontenehus,
        name,
        fontenehusCode,
        deleteSuggestionDays,
      });
    } catch (err: any) {
      handleApiError(err, "Feil ved oppdatering av fontenehus.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Laster...</p>;
  if (!targetFontenehus) return <p>Fontenehuset finnes ikke.</p>;

  return (
    <div className="pt-[90px] p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">
        Administrer {targetFontenehus.name}
      </h1>

      <div className="mb-4">
        <label className="block font-semibold mb-1">Navn:</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block font-semibold mb-1">
          Antall dager for sletting av udaterte lunsj forslag:
        </label>
        <input
          type="number"
          min={0} // prevents typing negative numbers in most browsers
          value={deleteSuggestionDays}
          onChange={(e) => {
            const value = Number(e.target.value);
            setDeleteSuggestionDays(value < 0 ? 0 : value); // clamp to 0
          }}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block font-semibold mb-1">Kode</label>
        <div className="relative flex items-center">
          <input
            type="text"
            value={fontenehusCode}
            disabled
            className="w-full border rounded px-3 py-2 pr-10"
          />
          <button
            type="button"
            onClick={() => {
              getNewFontenehusCode();
            }}
            className="absolute right-2 text-gray-500 hover:text-gray-700"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-orangeBrand text-white px-4 py-2 rounded hover:bg-orangeHover"
        >
          {saving ? "Lagrer..." : "Oppdater"}
        </button>

        {isSuperAdmin && (
          <button
            onClick={handleDelete}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Slett
          </button>
        )}

        <button
          onClick={() => navigate(-1)}
          className="bg-gray-300 px-4 py-2 rounded"
        >
          Tilbake
        </button>
      </div>
    </div>
  );
};

export default AdminFontenehusProfile;
